import { useCallback, useContext, useEffect, useState } from 'react';
import { API_BASE } from '../../../config/api';
import { UserContext } from '../../../context/UserContext';

// Server-wide UI preferences, backed by the app_settings table via
// GET/PUT /api/settings/ui (PUT is admin-only - the tabs hide their
// controls for non-admins the same way the Library tab already does).
// Keys are namespaced "ui.<tab>.<setting>" strings; values are the short
// strings the settings rows already work with ("FLAC", "true", "6"...).

const READABLE_KEY = /^ui\.[a-z0-9_.]{1,80}$/;

export function useUiPreferences<T extends Record<string, string>>(
    defaults: T
): [T, (patch: Partial<T>) => Promise<boolean>, boolean] {
    const { user } = useContext(UserContext);
    const [values, setValues] = useState<T>(defaults);
    const [loaded, setLoaded] = useState(false);
    const isAdmin = Boolean(user?.isAdmin);

    useEffect(() => {
        if (!isAdmin) {
            // Non-admins just run on defaults - the controls are hidden for
            // them anyway, so there's nothing to load.
            setLoaded(true);
            return;
        }
        let cancelled = false;
        fetch(`${API_BASE}/api/settings/ui`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : {}))
            .then((prefs: Record<string, string>) => {
                if (cancelled) return;
                const merged = { ...defaults };
                for (const key of Object.keys(defaults)) {
                    const value = prefs[key];
                    if (typeof value === 'string' && value !== '') {
                        (merged as Record<string, string>)[key] = value;
                    }
                }
                setValues(merged);
                setLoaded(true);
            })
            .catch(() => !cancelled && setLoaded(true));
        return () => {
            cancelled = true;
        };
        // defaults is a module-level constant per tab - not reactive state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const save = useCallback(
        async (patch: Partial<T>): Promise<boolean> => {
            setValues((prev) => ({ ...prev, ...patch }));
            if (!isAdmin) return false;
            try {
                const response = await fetch(`${API_BASE}/api/settings/ui`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(patch),
                });
                return response.ok;
            } catch {
                return false;
            }
        },
        [isAdmin]
    );

    return [values, save, loaded];
}

export { READABLE_KEY };
